import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";

/**
 * Filtered sign request list, tags and saved views — the endpoints gated on
 * the `envelope.advanced_filters` plan feature. Request and response shapes
 * are the backend's own (`SignRequestFilterBody` & co. in
 * `SignRequestsFiltersHelper`); mapping to and from the screen's model happens
 * there, not here.
 */

/** Body of the filtered list, and what a saved view stores as its `filters`. */
export interface SignRequestFilterBody {
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  signer?: string;
  senders?: string[];
  templateIds?: string[];
  tagIds?: string[];
  sort?: string;
  /**
   * The folder being browsed (0 = the root). Sent only while no filter is
   * applied; a filtered list cuts across every folder. Not part of a saved
   * view.
   */
  parentId?: number;
}

/** One page of requests matching `filters`, with the total across every page. */
export const fetchFilteredSignRequests = async (
  pageNumber: number,
  filters: SignRequestFilterBody,
) => {
  try {
    const callParams = await getCallParams("POST", filters);
    const response = await makeCall(
      `${urls.SIGN_FILTER_GET_PAGE}/${pageNumber}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignRequestSenders = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.SIGN_FILTER_GET_SENDERS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignRequestTags = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.SIGN_TAGS_GET, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createSignRequestTag = async (name: string, color: string) => {
  try {
    const callParams = await getCallParams("POST", { name, color });
    const response = await makeCall(urls.SIGN_TAG_CREATE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const updateSignRequestTag = async (
  tagId: string,
  name: string,
  color: string,
) => {
  try {
    const callParams = await getCallParams("PUT", { name, color });
    const response = await makeCall(
      `${urls.SIGN_TAG_UPDATE}/${tagId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteSignRequestTag = async (tagId: string) => {
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(
      `${urls.SIGN_TAG_DELETE}/${tagId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Replaces the request's whole tag set and returns the new one. */
export const setSignRequestTags = async (
  requestId: string,
  tagIds: string[],
) => {
  try {
    const callParams = await getCallParams("POST", { tagIds });
    const response = await makeCall(
      `${urls.SIGN_REQUEST_TAGS_SET}/${requestId}/tags`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const fetchSignRequestViews = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.SIGN_VIEWS_GET, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const createSignRequestView = async (
  name: string,
  filters: SignRequestFilterBody,
) => {
  try {
    const callParams = await getCallParams("POST", { name, filters });
    const response = await makeCall(urls.SIGN_VIEW_CREATE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** Renames a view; its filters are left as they are. */
export const renameSignRequestView = async (viewId: string, name: string) => {
  try {
    const callParams = await getCallParams("PUT", { name });
    const response = await makeCall(
      `${urls.SIGN_VIEW_UPDATE}/${viewId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const deleteSignRequestView = async (viewId: string) => {
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(
      `${urls.SIGN_VIEW_DELETE}/${viewId}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** `{ filters }`, or nothing (204) when the user has never filtered. */
export const fetchLastUsedSignRequestFilters = async () => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.SIGN_VIEWS_LAST_USED_GET, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export const saveLastUsedSignRequestFilters = async (
  filters: SignRequestFilterBody,
) => {
  try {
    const callParams = await getCallParams("POST", { filters });
    const response = await makeCall(urls.SIGN_VIEWS_LAST_USED_SAVE, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};
